import React, { useEffect, useState, useMemo } from "react";
import ProductCard from "../components/ProductCard";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

const HEADER_HEIGHT = 60;
const BOTTOM_NAV_HEIGHT = 60;

/* =====================================================
   PRODUCTS PAGE — FIRESTORE (FIXED LAYOUT)
===================================================== */
export default function Products() {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");

  /* ---------------------------------
     LOAD PRODUCTS
  --------------------------------- */
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "products"));

      const list = snap.docs
        .map(doc => ({
          product_id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => {
          // 1️⃣ Sort by updatedAt (latest first)
          const ta = a.updatedAt?.toDate?.();
          const tb = b.updatedAt?.toDate?.();
          if (ta && tb && ta.getTime() !== tb.getTime()) {
            return tb - ta;
          }

          // 2️⃣ Fallback: product_id number
          const na = Number(String(a.product_id).replace(/\D/g, ""));
          const nb = Number(String(b.product_id).replace(/\D/g, ""));
          return nb - na;
        });

      setAllProducts(list);
    } catch (err) {
      console.error("Failed to load products", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------
     FILTERED PRODUCTS
  --------------------------------- */
  const products = useMemo(() => {
    return allProducts.filter(p => {
      const matchSearch =
        !search ||
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.brand?.toLowerCase().includes(search.toLowerCase());

      const matchCategory =
        !category || p.category === category;

      const matchBrand =
        !brand || p.brand === brand;

      return matchSearch && matchCategory && matchBrand;
    });
  }, [allProducts, search, category, brand]);

  /* ---------------------------------
     FILTER OPTIONS
  --------------------------------- */
  const categories = useMemo(
    () => [...new Set(allProducts.map(p => p.category).filter(Boolean))],
    [allProducts]
  );

  const brands = useMemo(
    () => [...new Set(allProducts.map(p => p.brand).filter(Boolean))],
    [allProducts]
  );

  /* ---------------------------------
     RENDER
  --------------------------------- */
  return (
    <div style={styles.page}>
      <div style={styles.scroll}>
        {/* FILTERS */}
        <div style={styles.filterBox}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            style={styles.search}
          />

          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={styles.dropdown}
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={brand}
            onChange={e => setBrand(e.target.value)}
            style={styles.dropdown}
          >
            <option value="">All Brands</option>
            {brands.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>

        {/* CONTENT */}
        {loading && <div style={styles.loading}>Loading products…</div>}

        {!loading && products.length === 0 && (
          <div style={styles.empty}>No products found</div>
        )}

        {!loading && products.length > 0 && (
          <div style={styles.grid}>
            {products.map(p => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================
   STYLES — FIXED HEADER & FOOTER SAFE
===================================================== */
const styles = {
   page: {
    background: "#f9fafb",
    paddingBottom: BOTTOM_NAV_HEIGHT, // ✅ THIS FIXES IT
  },

  scroll: {
    height: `calc(100dvh - ${HEADER_HEIGHT}px)`,
    overflowY: "auto",
    padding: 12,
    paddingTop: HEADER_HEIGHT + 12,
  },

  filterBox: {
    background: "#fff",
    borderRadius: 12,
    padding: 10,
    marginTop: 8,
    marginBottom: 12,
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    display: "grid",
    gap: 8,
  },
  search: {
    width: "100%",
    height: 38,
    padding: "0 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
  },
  dropdown: {
    width: "100%",
    height: 38,
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
  },
  loading: {
    textAlign: "center",
    marginTop: 40,
    color: "#6b7280",
  },
  empty: {
    textAlign: "center",
    marginTop: 40,
    color: "#6b7280",
  },
};
